import './adjacent_target.scss';
import { h } from 'preact';
import * as is from 'utilities/is';
import { RenderTarget } from './render_target';
import { ANNOTATION_TARGETS } from 'constants';
import { getStateLogger } from 'utilities/state';
import { accessibleContainer } from 'utilities/accessibility';
import { EXPANDABLE } from 'rendering/annotations/taxonomies';
import {
    ANNOTATION_EVENT,
    PLAYER_EVENT,
    PLAYER_CONTROL_EVENT
} from 'constants/events';

const PLAYBACK_CONTROL_INELIGIBLE = ['quiz'];

export class AdjacentTarget extends RenderTarget {
    constructor(props) {
        super(props);

        if (this.expandable) {
            this.state.showExpanded = this.showExpanded;
            this.state.startExpanded = this.startExpanded;
            this.state.expanded = this.startExpanded;
        }

        this.state.scale = this.props.scale || 1;

        props.bus.on(PLAYER_EVENT.SCALE_CHANGE, this.onPlayerScaleChange);
        props.bus.on(ANNOTATION_EVENT.EXPAND_TARGET, this.onRemoteExpand);
        props.bus.on(ANNOTATION_EVENT.CONTRACT_TARGET, this.onRemoteCollapse);
    }

    componentDidUpdate(props) {
        if (!this.state.startExpanded && this.startExpanded) {
            this.setState({
                startExpanded: true,
                expanded: true
            });
        } else if (this.state.startExpanded && !this.startExpanded) {
            this.setState({
                startExpanded: false,
                expanded: false
            });
        }
    }

    componentWillUnmount() {
        const { bus } = this.props;

        bus.off(PLAYER_EVENT.SCALE_CHANGE, this.onPlayerScaleChange);
        bus.off(ANNOTATION_EVENT.EXPAND_TARGET, this.onRemoteExpand);
        bus.off(ANNOTATION_EVENT.CONTRACT_TARGET, this.onRemoteCollapse);
    }

    onClick() {
        if (this.expandable && !this.state.expanded) {
            this.setExpanded(true);
        }
    }

    onRemoteExpand = target => {
        if (target && this.props.target !== target) {
            return;
        }

        this.setExpanded(true);
    }

    onRemoteCollapse = target => {
        if (target && this.props.target !== target) {
            return;
        }

        this.setExpanded(false);
    }

    // Temporary hack to size annotations to fit
    // Should eventually use another method to determine container scale
    onPlayerScaleChange = scale => this.setState({scale})

    setExpanded(expanded) {
        if (this.expandManagesPlayback) {
            if (expanded === true) {
                this.props.bus && this.props.bus.emit(PLAYER_CONTROL_EVENT.PAUSE);
            } else if (expanded === false) {
                this.props.bus && this.props.bus.emit(PLAYER_CONTROL_EVENT.PLAY);
            }
        }
        
        this.setState(
            () => ({ expanded }),
            () => this.logStateChange(this.state)
        );
    }

    toggleExpanded = e => {
        this.setExpanded(!this.state.expanded);
        e.stopPropagation();
    }

    logStateChange(state) {
        this.targetList
            .filter(a => this.isExpandable(a.type))
            .forEach(a => getStateLogger().logAnnotationState(a.id, state));
    }

    onRef(el) {
        this.el = el;
    }

    render() {
        return (
            <div { ...this.attributes }>
                <div { ...this.scaleContainerAttributes }>
                    { this.scalableAnnotations }
                </div>
                <div { ...this.nonScalableContainerAttributes }>
                    { this.nonScalableAnnotations }
                </div>
            </div>
        );
    }

    isExpandable(annotationType) {
        return EXPANDABLE.includes(annotationType);
    }

    get attributes() {
        return {
            ref: this.onRef.bind(this),
            className: this.containerClasses,
            style: this.targetStyles,
            'data-annotation-count': this.annotations.length,
            'data-target': 'adjacent_target',
            'data-animate': this.shouldAnimate,
            'data-show-expanded': this.shouldShowExpandable,
            'data-expanded': this.state.expanded ? 'true' : 'false',
            'data-expandable': this.expandable ? 'true' : 'false'
        }
    }

    get containerAttributes() {
        return {
            ...accessibleContainer,
            style: {},
            className: 'hapyak-annotation-container active'
        }
    }

    get nonScalableContainerAttributes() {
        return {
            'data-scale-final': 'true',
            ...this.containerAttributes
        }
    }

    get scaleContainerAttributes() {
        const { className } = this.containerAttributes;
        const scaleFinal = this.state.scaleFinalized ? 'true' : 'false';

        return {
            ...this.containerAttributes,
            style: this.scaleContainerStyles,
            className: `${className} scaleable-container`,
            'data-scale-final': scaleFinal
        }
    }

    get scaleContainerStyles() {
        const { scale } = this.state;
        const width = `${1/scale*100}%`;
        const height = width;

        return {
            width,
            height,
            transform: `scale(${scale})`,
            transformOrigin: 'left top'
        }
    }

    get containerClasses() {
        return [
            this.targetName,
            'adjacent-target',
            this.state.expanded ? '' : 'hide-scroll',
            this.state.expandable && !this.state.expanded ? 'clickable' : ''
        ].join(' ');
    }

    get expandable() {
        return this.targetList.some(annotation => this.isExpandable(annotation.type) && annotation.properties.showExpanded !== false);
    }

    get startExpanded() {
        return this.targetList.some(annotation => annotation.properties.startExpanded);
    }

    get shouldAnimate() {
        return this.targetList.some(annotation => annotation.properties.animate);
    }

    get shouldShowExpandable() {
        return this.targetList.some(annotation => annotation.properties.showExpanded);
    }

    get expandManagesPlayback() {
        return this.targetList.some(({type = ''}) => !PLAYBACK_CONTROL_INELIGIBLE.includes(type));
    }

    get expandHandler() {
        if (this.targetName === ANNOTATION_TARGETS.ADJACENT_TOP || this.targetName === ANNOTATION_TARGETS.ADJACENT_BOTTOM) {
            return this.toggleExpandVertical;
        }

        return this.toggleExpandHorizontal;
    }

    get commonAttributes() {
        return {
            ...super.commonAttributes,
            expanded: this.state && this.state.expanded
        }
    }
}